import assert from 'assert';
import {ScaleLinear} from 'd3';
import * as d3 from 'd3';

import {NucleotideType} from 'biojs/BioConstants';
import {Part} from 'biojs/Part';
import {Sequence} from 'components/Sequence';
import {LayeringMode} from 'constants/MapsConstants';
import {BiointelligentLinkType} from 'molbio/biointelligent-links/BiointelligentLinkConstants';
import {
  BiointelligentLinkAnnotation,
  BiointelligentLinkHighlightRegion,
  PART_ANNOTATION_COLOR,
  PART_ANNOTATION_OUT_OF_SYNC_COLOR,
  PART_LINK_ARROW_COLOR,
  LEGACY_PART_HIGHLIGHT_OUT_OF_SYNC_COLOR,
  LINK_HIGHLIGHT_COLOR,
  LINK_HIGHLIGHT_FILL_OPACITY,
  LINK_HIGHLIGHT_HOVER_COLOR,
  PART_ANNOTATION_ARCHIVED_COLOR,
  ViewBiointelligentLinkData,
  PART_LINK_CHIP_TEXT_COLOR,
  TRANSCRIPTION_COLOR,
  TRANSCRIPTION_CHIP_TEXT_COLOR,
  TRANSCRIPTION_ARROW_COLOR,
} from 'molbio/maps/BiointelligentLinksConstants';
import {getTranscriptionLayers, transcriptionId} from 'molbio/maps/linear/TranscriptionHelpers';
import {LayersData} from 'molbio/maps/MapsUtil';
import {PartHighlightRegion} from 'molbio/maps/translateLinksToHighlightRegions';
import * as sequtil from 'molbio/SeqUtil';
import {TranscriptionMetadata} from 'molbio/TranscriptionsUtil';

export function isPartLinkType(linkType: BiointelligentLinkType): boolean {
  return [
    BiointelligentLinkType.RNA_PART,
    BiointelligentLinkType.DNA_PART,
    BiointelligentLinkType.AMINO_ACID_PART,
  ].includes(linkType);
}

export function isPartLinkRegion(
  linkRegion: BiointelligentLinkHighlightRegion
): linkRegion is PartHighlightRegion {
  return (
    linkRegion.links.every((link) => isPartLinkType(link.linkType)) &&
    linkRegion.hasOwnProperty('partRefSequenceId')
  );
}

export function isNonOverlappingPartLinkRegion(
  linkRegion: BiointelligentLinkHighlightRegion
): linkRegion is PartHighlightRegion {
  return (
    isPartLinkRegion(linkRegion) && linkRegion.links.length === 1 && linkRegion.partRefSequenceId != null
  );
}

export function getRectangularPath(
  partRegion: BiointelligentLinkHighlightRegion,
  sliceStart: number,
  sliceEnd: number,
  xFn: ScaleLinear<number, number>,
  y: number,
  height: number,
  sequenceLength: number
): string {
  const start = Math.max(sliceStart, partRegion.start);
  const end = Math.min(
    sliceEnd,
    partRegion.start + sequtil.length(partRegion.start, partRegion.end, sequenceLength)
  );

  const xStart = xFn(start);
  const xEnd = xFn(end);
  return `M ${xStart} ${y} L ${xEnd} ${y} L ${xEnd} ${y + height} L ${xStart} ${y + height} Z`;
}

export function getHighlightColor(
  region: BiointelligentLinkHighlightRegion,
  hoveredLinkIndices: {start: number; end: number} | null,
  forLegacyParts: boolean = false
): string {
  if (forLegacyParts) {
    assert(
      isNonOverlappingPartLinkRegion(region),
      'Legacy parts only support non-overlapping nucleotide parts'
    );
    // Legacy parts have a special "out of sync" color that we don't apply for the new parts view.
    return isNonOverlappingPartLinkRegion(region) && region.outOfSync
      ? d3.rgb(LEGACY_PART_HIGHLIGHT_OUT_OF_SYNC_COLOR).toString()
      : d3.rgb(LINK_HIGHLIGHT_COLOR).toString();
  }

  const regionIsHovered =
    hoveredLinkIndices != null &&
    sequtil.isRangeWithin(region.start, region.end, hoveredLinkIndices.start, hoveredLinkIndices.end);
  return regionIsHovered
    ? d3.rgb(LINK_HIGHLIGHT_HOVER_COLOR).toString()
    : d3.rgb(LINK_HIGHLIGHT_COLOR).toString();
}

export function getHighlightOpacity(
  region: BiointelligentLinkHighlightRegion,
  useOverlappingPartsStyling: boolean
): number {
  let fillOpacity = LINK_HIGHLIGHT_FILL_OPACITY;
  // For non-overlapping parts, make the shading lighter to indicate archived. For
  // overlapping parts, the part annotation is lighter.
  if (!useOverlappingPartsStyling && isNonOverlappingPartLinkRegion(region) && region.isArchived) {
    fillOpacity *= 0.5;
  }
  return fillOpacity;
}

export function getTopLineColor(linkType: BiointelligentLinkType, outOfSync: boolean = false): string {
  switch (linkType) {
    case BiointelligentLinkType.TRANSCRIPTION:
      return d3.rgb(TRANSCRIPTION_COLOR).toString();
    default: {
      const highlightColor = outOfSync
        ? d3.rgb(PART_ANNOTATION_OUT_OF_SYNC_COLOR)
        : d3.rgb(PART_ANNOTATION_COLOR);
      return highlightColor.toString();
    }
  }
}

export function getLinkAnnotationColor(link: BiointelligentLinkAnnotation): string {
  switch (link.linkType) {
    case BiointelligentLinkType.TRANSCRIPTION:
      return TRANSCRIPTION_COLOR;
    default: {
      if (!link.isInSync) {
        return PART_ANNOTATION_OUT_OF_SYNC_COLOR;
      } else if (link.isArchived) {
        return PART_ANNOTATION_ARCHIVED_COLOR;
      } else {
        return PART_ANNOTATION_COLOR;
      }
    }
  }
}

export function getLinkChipTextColor(linkType: BiointelligentLinkType): string {
  // TODO: Add translations colors.
  switch (linkType) {
    case BiointelligentLinkType.RNA_PART:
    case BiointelligentLinkType.DNA_PART:
      return PART_LINK_CHIP_TEXT_COLOR;
    case BiointelligentLinkType.TRANSCRIPTION:
      return TRANSCRIPTION_CHIP_TEXT_COLOR;
    default:
      return PART_LINK_CHIP_TEXT_COLOR;
  }
}

export function getLinkArrowColor(linkType: BiointelligentLinkType): string {
  // TODO: Add translations colors.
  switch (linkType) {
    case BiointelligentLinkType.RNA_PART:
    case BiointelligentLinkType.DNA_PART:
      return PART_LINK_ARROW_COLOR;
    case BiointelligentLinkType.TRANSCRIPTION:
      return TRANSCRIPTION_ARROW_COLOR;
    default:
      return PART_LINK_ARROW_COLOR;
  }
}

function sortLinksByLength(
  links: Array<ViewBiointelligentLinkData>,
  containerSequenceLength: number
): Array<ViewBiointelligentLinkData> {
  return links.toSorted(
    (a, b) =>
      sequtil.length(b.start, b.end, containerSequenceLength) -
      sequtil.length(a.start, a.end, containerSequenceLength)
  );
}

function uniqueLinksForHighlightRegions(
  linkRegions: Array<BiointelligentLinkHighlightRegion>
): Array<ViewBiointelligentLinkData> {
  return Object.values(
    Object.fromEntries(linkRegions.flatMap((region) => region.links).map((l) => [l.id, l]))
  );
}

/**
 * Calculates how to layer the colored lines bordering the top or bottom of the highlight.
 *
 * Each highlight region has two parts: The shaded background on the sequence map, and thin colored
 * lines that border the top or bottom of the highlight, corresponding to each link in the highlight region.
 * This function calculates which layer each line belongs on.
 * For example, if we have three part links in a slice that all overlap each other, we have three line layers:
 *        ---
 *    ------
 *      ----------
 *    GATTACAGATTACA
 *    TCGGCACTCGGCAC
 *
 * Links are assigned layers based on their type and length - within a type, the longest link goes on the
 * layer closest to the residues.
 */
export function calculateLinkLineLayers(
  linkRegionsInSlice: Array<BiointelligentLinkHighlightRegion>,
  containerSequenceLength: number
): {[linkId: string]: number} {
  const nLayers = linkRegionsInSlice.reduce((max, region) => Math.max(max, region.links.length), 0);
  const sortedLinksInSlice = sortLinksByLength(
    uniqueLinksForHighlightRegions(linkRegionsInSlice),
    containerSequenceLength
  );
  return sortedLinksInSlice.reduce(
    (acc, link, i) => {
      acc[link.id] = Math.min(i, nLayers - 1);
      return acc;
    },
    {} as {[key: string]: number}
  );
}

interface ModelToLinkAnnotationParams {
  containerSequence: Sequence;
  layeringMode: LayeringMode;
  linkType: BiointelligentLinkType;
}

interface PartModelToLinkAnnotationParams extends ModelToLinkAnnotationParams {
  part: Part;
}

/** Same behavior as a part's click and dblClick methods from client/biojs/Part.ts. */
function onClickAnnotation(sequence: Sequence, start: number, end: number, shiftKey: boolean): void {
  const selection = sequence.get('selection');
  if (shiftKey) {
    selection.expandRange(start, end);
  } else {
    selection.setEndpoints(start, end);
  }
}

function onDoubleClickAnnotation(sequence: Sequence, start: number, end: number, shiftKey: boolean): void {
  onClickAnnotation(sequence, start, end, shiftKey);
}

function partModelToLinkAnnotation({
  containerSequence,
  layeringMode,
  part,
  linkType,
}: PartModelToLinkAnnotationParams): BiointelligentLinkAnnotation {
  return {
    id: part.id.toString(),
    start: part.start(),
    end: part.end(),
    drawStart: part.drawStart(containerSequence),
    drawEnd: part.drawEnd(containerSequence),
    name: part.name(),
    isInSync: part.isInSync(containerSequence._sequence()),
    layerIndex: part.get(layeringMode),
    linkedEntityId: part.sequence().id,
    isArchived: part.sequence().isArchived(),
    isSelected: part.selected(),
    direction: part.reversed() && part.complemented() ? -1 : 1,
    linkType,
    onClick: (sequence, shiftKey) => {
      part.click(sequence, shiftKey);
    },
    onDoubleClick: (sequence, shiftKey) => {
      part.dblClick(sequence, shiftKey);
    },
  };
}

function transcriptionModelToLinkAnnotation(
  layersData: LayersData<TranscriptionMetadata & {cid: string}>,
  transcription: TranscriptionMetadata
): BiointelligentLinkAnnotation {
  return {
    id: transcriptionId(transcription),
    start: transcription.start,
    end: transcription.end,
    drawStart: transcription.start,
    drawEnd: transcription.end,
    name: transcription.rnaSequence.name,
    isInSync: true,
    layerIndex:
      layersData.layers.find((layer) => layer.model.cid === transcriptionId(transcription))?.layer ?? 0,
    linkedEntityId: transcription.rnaSequence.id,
    isArchived: false,
    isSelected: false,
    direction: transcription.isReverseComplement ? -1 : 1,
    linkType: BiointelligentLinkType.TRANSCRIPTION,
    onClick: (sequence, shiftKey) => {
      onClickAnnotation(sequence, transcription.start, transcription.end, shiftKey);
    },
    onDoubleClick: (sequence, shiftKey) => {
      onDoubleClickAnnotation(sequence, transcription.start, transcription.end, shiftKey);
    },
  };
}

interface TranscriptionModelsToLinkAnnotationsParams extends ModelToLinkAnnotationParams {
  transcriptionModels: Array<TranscriptionMetadata>;
}

function transcriptionModelsToLinkAnnotations({
  containerSequence,
  transcriptionModels,
}: TranscriptionModelsToLinkAnnotationsParams): Array<BiointelligentLinkAnnotation> {
  const layersData = getTranscriptionLayers(transcriptionModels, containerSequence.length());
  return transcriptionModels.map((transcription) =>
    transcriptionModelToLinkAnnotation(layersData, transcription)
  );
}

interface ModelsToLinkAnnotationsParams extends ModelToLinkAnnotationParams {
  models: Array<Part> | Array<TranscriptionMetadata>;
}

export function modelsToLinkAnnotations({
  containerSequence,
  layeringMode,
  models,
  linkType,
}: ModelsToLinkAnnotationsParams): Array<BiointelligentLinkAnnotation> {
  switch (linkType) {
    case BiointelligentLinkType.RNA_PART:
    case BiointelligentLinkType.DNA_PART: {
      assert(
        (containerSequence.nucleotideType() === NucleotideType.DNA &&
          linkType === BiointelligentLinkType.DNA_PART) ||
          (containerSequence.nucleotideType() === NucleotideType.RNA &&
            linkType === BiointelligentLinkType.RNA_PART),
        'Part nucleotide type did not match container sequence nucleotide type.'
      );
      return models.map((part) =>
        partModelToLinkAnnotation({
          containerSequence,
          layeringMode,
          part: part as Part,
          linkType,
        })
      );
    }
    case BiointelligentLinkType.TRANSCRIPTION:
      return transcriptionModelsToLinkAnnotations({
        containerSequence,
        layeringMode,
        linkType,
        transcriptionModels: models as Array<TranscriptionMetadata>,
      });
    default:
      assert(false, `Unsupported link type: ${linkType}`);
  }
}
